import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@perxis-auto/ui-kit/lib/components/Button';
import { Col, Grid, Row } from '@perxis-auto/ui-kit/lib/components/Grid';
import { Icon } from '@perxis-auto/ui-kit/lib/components/Icon';
import { Parameters } from '@perxis-auto/ui-kit/lib/components/Parameters';

import { GET_DEFAULT_FILTERS } from '@/constants/filters';
import { useConstructor } from '@/context/constructor';
import getDependantFiltersToReset from '@/features/filters/helpers/getDependantFiltersToReset';
import useAdjustedFilterGrid from '@/features/filters/hooks/useAdjustedFilterGrid';
import useFiltersData from '@/features/filters/hooks/useFiltersData';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { rootActions, rootSelectors } from '@/features/store';
import { withHiddenLazyComponent } from '@/hoc';
import useAvailableHeight from '@/hooks/useAvailableHeight';
import useMatchMedia, { BreakPoints } from '@/hooks/useMatchMedia';
import {
  ConstructorType,
  IFilterGrid,
  IFilterGridItem,
} from '@/interfaces/constructor.interfaces';
import GridUtils from '@/utils/gridUtils';
import instanceOfGrid from '@/utils/instanceOfGrid';

import { FilterItemGroup, FiltersWrapper } from './components';
import { FiltersProps } from './Filters.types';

import styles from './Filters.module.scss';

function Filters(props: FiltersProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { grid } = useConstructor();

  const filterGrid = useMemo(() => {
    const filtersGrid =
      GridUtils.findByCondition(grid, e => {
        return instanceOfGrid(e) && e.type === ConstructorType.Filters;
      }) ?? GET_DEFAULT_FILTERS();
    return filtersGrid as IFilterGrid;
  }, [grid]);

  /* Selectors */
  const visible = useAppSelector(rootSelectors.choice.showFiltersSelector);
  const filterChoice = useAppSelector(rootSelectors.choice.filtersChoiceSelector);
  const filtersIsSelected = useAppSelector(
    rootSelectors.choice.isFiltersSelectedSelector,
  );

  const isMobile = useMatchMedia(BreakPoints.smMax);
  const ref = useRef(null);
  const { height } = useAvailableHeight(ref);

  const adjustedFilterGrid = useAdjustedFilterGrid(filterGrid);

  const { isLoading, isUninitialized } = useFiltersData();
  const isFiltersLoading = isLoading || isUninitialized || !adjustedFilterGrid;

  const [activeGroups, setActiveGroups] = useState(
    filterGrid.items.map(({ id }) => String(id)),
  );

  const filterGroups = useMemo(() => {
    const grid = isFiltersLoading ? filterGrid : adjustedFilterGrid;
    return grid.items.filter(item => !item.hidden);
  }, [adjustedFilterGrid, filterGrid, isFiltersLoading]);

  const handleToggle = () => {
    dispatch(rootActions.choice.toggleFilters());
  };

  const handleReset = () => {
    dispatch(rootActions.choice.resetFilters());
  };

  const handleGroupChange = (changedGroups: string[]) => {
    setActiveGroups(changedGroups);
  };

  /**
   * Обрабатывает изменение значения фильтра
   * @param key ключ изменившегося фильтра
   */
  const handleFilterChange = (key: string) => {
    const resetFilters = getDependantFiltersToReset(key, filterGrid, filterChoice);

    if (!resetFilters.length) {
      return;
    }

    // Подготавливаем объект значений фильтров в формате, подходящем для стора.
    const filterValues: { [key: string]: unknown } = {};
    resetFilters.forEach(filterKey => {
      filterValues[filterKey] = null;
    });

    dispatch(rootActions.choice.setFilters(filterValues));
  };

  return (
    <aside ref={ref} className={styles.filters} style={{ height: height || 'auto' }}>
      <FiltersWrapper
        title={t('filters.title')}
        type={isMobile ? 'mobile' : 'desktop'}
        visible={visible}
        toggle={handleToggle}
        className={styles.filtersWrapper}
      >
        {!!filterGroups && (
          <Parameters
            activeGroups={activeGroups}
            groups={filterGroups}
            isLoading={isFiltersLoading}
            onChange={handleGroupChange}
            className={styles.filtersParameters}
            footer={
              filtersIsSelected ? (
                <Row gutter={[18, 'vertical-sky']}>
                  {isMobile && (
                    <Col>
                      <Button
                        type="primary"
                        icon={<Icon name="forward" sticker="filters-apply-icon" />}
                        onClick={handleToggle}
                      >
                        {t('filters.apply')}
                      </Button>
                    </Col>
                  )}
                  <Col>
                    <Button
                      type="ghost"
                      icon={<Icon name="cross" sticker="filters-reset-icon" />}
                      onClick={handleReset}
                    >
                      {t('filters.reset')}
                    </Button>
                  </Col>
                </Row>
              ) : null
            }
          >
            {group => {
              return (
                <Grid
                  grid={group}
                  renderItem={(item: IFilterGridItem) => (
                    <FilterItemGroup filterKey={item.key} onChange={handleFilterChange} />
                  )}
                />
              );
            }}
          </Parameters>
        )}
      </FiltersWrapper>
    </aside>
  );
}

export default withHiddenLazyComponent(
  Filters,
  rootSelectors.config.filtersEnableSelector,
);
